import type { ReactNode, SyntheticEvent } from 'react';
import React from 'react';
import type { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Provider, Root, Viewport } from '@radix-ui/react-toast';
import { faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import {
  faClock,
  faInfoCircle,
  faTriangleExclamation,
} from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { faCircleCheck } from '@soundxyz/font-awesome/sharp-solid-svg-icons/index.mjs';
import type { CSS } from '@components-shared/src/stitches';
import { keyframes, styled, theme } from '@components-shared/src/stitches';
import { IconButton } from '../icon-buttons/IconButton';
import { AccessibleLink } from '../link/AccessibleLink';
import { MOBILE_NAV_HEIGHT } from '../mobile-nav/constants';

const TOAST_DURATION = 3000;

export const ToastVariant = {
  PRIMARY: 'primary',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  PENDING: 'pending',
} as const;

export interface ToastProps {
  alignment?: 'left' | 'right';
  ctaOnClick?: (event: SyntheticEvent) => void;
  ctaHref?: string;
  ctaText?: string;
  duration?: number;
  isToastOpen: boolean;
  setToastOpen: (isOpen: boolean) => void;
  text: ReactNode;
  variant?: (typeof ToastVariant)[keyof typeof ToastVariant];
  withAudioPlayer?: boolean;
  inverted?: boolean;
  withExpandedAudioPlayer?: boolean;
}

const CtaElement = ({
  ctaHref,
  ctaText,
  ctaOnClick,
}: {
  ctaHref?: string;
  ctaText: string;
  ctaOnClick: (e: SyntheticEvent<Element, Event>) => void;
}) => {
  return ctaHref ? (
    <CallToActionLink absoluteUri={ctaHref} type="external">
      {ctaText}
    </CallToActionLink>
  ) : (
    <Cta onClick={ctaOnClick}>{ctaText}</Cta>
  );
};

export const Toast = ({
  alignment = 'right',
  ctaOnClick = () => null,
  ctaHref,
  ctaText,
  duration = TOAST_DURATION,
  isToastOpen,
  setToastOpen,
  text,
  variant = ToastVariant.PRIMARY,
  withAudioPlayer = false,
  inverted = false,
  withExpandedAudioPlayer = false,
}: ToastProps) => {
  let variantIcon: FontAwesomeIconProps['icon'];
  let variantIconColor: string;

  switch (variant) {
    case ToastVariant.SUCCESS:
      variantIcon = faCircleCheck;
      variantIconColor = theme.colors.brand300.toString();
      break;
    case ToastVariant.WARNING:
      variantIcon = faTriangleExclamation;
      variantIconColor = '#FBBF24';
      break;
    case ToastVariant.ERROR:
      variantIcon = faTriangleExclamation;
      variantIconColor = theme.colors.destructive300.toString();
      break;
    case ToastVariant.PENDING:
      variantIcon = faClock;
      variantIconColor = theme.colors.warning400.toString();
      break;
    case ToastVariant.PRIMARY:
    default:
      variantIcon = faInfoCircle;
      variantIconColor = inverted ? theme.colors.black.toString() : theme.colors.white.toString();
      break;
  }

  return (
    <Provider>
      <ToastContainer
        open={isToastOpen}
        duration={duration}
        onOpenChange={setToastOpen}
        inverted={inverted}
      >
        <FontAwesomeIcon icon={variantIcon} color={variantIconColor} size="xl" />
        <div>
          <p>{text}</p>
          {!!ctaText ? <br /> : ' '}
          {ctaText && <CtaElement ctaHref={ctaHref} ctaText={ctaText} ctaOnClick={ctaOnClick} />}
        </div>
        <IconButton
          variant={inverted ? 'primary' : 'inverted'}
          onClick={() => setToastOpen(false)}
          iconSize={16}
          icon={<FontAwesomeIcon icon={faXmark} size="lg" />}
          css={{
            flexShrink: 0,
          }}
        />
      </ToastContainer>
      <ToastViewport
        alignment={alignment}
        withAudioPlayer={withAudioPlayer && !withExpandedAudioPlayer}
        withExpandedAudioPlayer={withExpandedAudioPlayer}
      />
    </Provider>
  );
};

const hide = keyframes({
  '0%': { opacity: 1 },
  '100%': { opacity: 0 },
});

const slideIn = keyframes({
  from: { transform: `translateY(calc(100% + 24px))` },
  to: { transform: 'translateY(0)' },
});

const ToastContainer = styled(Root, {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 19,
  boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.04), 0px 6px 20px rgba(0, 0, 0, 0.2)',
  borderRadius: 8,
  base: 'M',
  fontStyle: 'medium',
  marginBottom: 12,
  padding: 16,
  marginX: 12,
  alignItems: 'center',

  div: {
    width: '100%',
  },

  p: {
    display: 'inline',
    overflow: 'hidden',
  },

  '@tablet': {
    marginX: 40,
    marginBottom: 40,
    base: 'L',
  },

  '@media (prefers-reduced-motion: no-preference)': {
    '&[data-state="open"]': {
      animation: `${slideIn} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
    },
    '&[data-state="closed"]': {
      animation: `${hide} 100ms ease-in`,
    },
  },

  variants: {
    inverted: {
      true: {
        backgroundColor: '$white',
        color: '$neutral900',

        p: {
          color: '$neutral900',
        },
      },
      false: {
        backgroundColor: '$neutral900',
        color: '$white',

        p: {
          color: '$white',
        },
      },
    },
  },
});

const ToastViewport = styled(Viewport, {
  position: 'fixed',
  bottom: `calc(${MOBILE_NAV_HEIGHT}px + env(safe-area-inset-bottom))`,
  listStyle: 'none',
  zIndex: '$toast',
  outline: 'none',
  width: '100%',
  '@tablet': {
    bottom: 0,
    width: 480,
  },
  variants: {
    alignment: {
      left: {
        left: 0,
      },
      right: {
        right: 0,
      },
    },
    withAudioPlayer: {
      true: {
        // 56px it's the Minimized Player Height (can't import because lives in webapp)
        bottom: `calc(${MOBILE_NAV_HEIGHT}px + 56px + env(safe-area-inset-bottom))`,

        '@tablet': {
          bottom: 0,
        },
      },
    },
    withExpandedAudioPlayer: {
      true: {
        bottom: 0,
      },
    },
  },
});

const ctaStyles: CSS = {
  color: '$brand300',
  '&:hover': {
    color: '$brand600',
  },
};

const Cta = styled('button', ctaStyles);
const CallToActionLink = styled(AccessibleLink, ctaStyles);
